import { AloftArea, AloftDate, isDefined } from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { RootState } from '../../../types.ts';
import { selectEffectiveDate } from '../orderSelectors.ts';
import { selectImprovementsById } from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectOpinionOfSiteValuePerSFDisplay } from '../selectOpinionOfSiteValuePerSF.ts';
import { selectSiteCostPerSfDisplay } from '../selectSiteCostPerSf.ts';
import { selectSiteSizes } from '../selectSiteSize.ts';

const formatSiteSize = (siteSize: AloftArea | undefined): string => {
  if (!isDefined(siteSize)) return '-';
  const numericValue = siteSize.toSqFt().unitAmount;
  return `${numericValue.toLocaleString('en-US')} sf`;
};

const formatPricePerSf = (pricePerSf: string | undefined): string => {
  if (!isDefined(pricePerSf)) return '-';

  const raw = pricePerSf.trim();
  if (raw === '') return '-';
  const isParenNegative = raw.startsWith('(') && raw.endsWith(')');
  const cleaned = raw
    .replace(/\/\s*sf\b/gi, '')
    .replace(/[()]/g, '')
    .replace(/,/g, '')
    .replace(/[^\d.-]/g, '');

  const numericValue = parseFloat(cleaned);
  if (Number.isNaN(numericValue)) return '-';
  if (numericValue === 0) return '-';
  const roundedValue = Math.round(numericValue);
  const signedRoundedValue = isParenNegative ? -Math.abs(roundedValue) : roundedValue;
  return `$ ${signedRoundedValue.toLocaleString('en-US')} /sf`;
};

const selectSummaryDataById = createSelector(
  [
    (state: RootState, id: string) => selectImprovementsById(state, id),
    selectEffectiveDate,
    selectSiteSizes,
    (state: RootState, id: string) => {
      if (id === 'subject') {
        return selectOpinionOfSiteValuePerSFDisplay(state);
      }
      return selectSiteCostPerSfDisplay(state, id);
    },
    (_state: RootState, id: string) => id,
  ],
  (improvements, effectiveDate, siteSizes, pricePerSf, id) => {
    const actualAge = improvements?.age;
    const effectiveDateParsed = isDefined(effectiveDate)
      ? AloftDate.schema.parse(effectiveDate)
      : undefined;
    const yearBuilt =
      isDefined(actualAge) && isDefined(effectiveDateParsed)
        ? effectiveDateParsed.getYear() - actualAge
        : undefined;
    const siteSizeArea = siteSizes?.[id];
    const siteSize = isDefined(siteSizeArea) ? siteSizeArea.toSqFt() : undefined;

    return {
      actualAge,
      yearBuilt,
      siteSize,
      pricePerSf,
    };
  },
);

export type SummaryDataDisplay = {
  actualAgeDisplay: string;
  yearBuiltDisplay: string;
  siteSizeDisplay: string;
  pricePerSfDisplay: string;
};

export const selectSummaryDataDisplayById = createSelector(
  [selectSummaryDataById, (_state: RootState, id: string) => id],
  (summaryData, id): SummaryDataDisplay => {
    const isSubject = id === 'subject' || id === 'subjectArv';
    const actualAgeDisplay = isDefined(summaryData.actualAge)
      ? `${summaryData.actualAge} yrs`
      : '-';
    const yearBuiltDisplay = isDefined(summaryData.yearBuilt)
      ? summaryData.yearBuilt.toString()
      : '-';
    const siteSizeDisplay = formatSiteSize(summaryData.siteSize);
    const pricePerSfDisplay = isSubject
      ? (summaryData.pricePerSf ?? '')
      : formatPricePerSf(summaryData.pricePerSf);

    return {
      actualAgeDisplay,
      yearBuiltDisplay,
      siteSizeDisplay,
      pricePerSfDisplay,
    };
  },
);
